import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CalendarIcon, Loader2 } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';
import { formatCompact, formatGallons, formatLcdRead, paddedDomain, unitLabel } from '../lib/reads';
import { useMeterReads } from '../hooks/useMeterReads';

interface MeterLifetimeConsumptionChartProps {
  sensorId: string | undefined;
  /**
   * When provided, the chart is controlled by this range (the page's shared
   * date-range toolbar) and hides its own timespan selector. When omitted, the
   * chart manages its own selector (90d/1yr/All + custom range) for long-term
   * analysis.
   */
  dateRange?: { from: Date; to: Date };
}

type PresetKey = '90d' | '1yr' | 'all' | 'custom';

// `read` data begins 2026-02-28; an earlier floor returns everything available.
const ALL_START = new Date('2026-01-01T00:00:00Z');

const PRESETS: { key: Exclude<PresetKey, 'custom'>; label: string }[] = [
  { key: '90d', label: '90d' },
  { key: '1yr', label: '1yr' },
  { key: 'all', label: 'All' },
];

function presetRange(key: Exclude<PresetKey, 'custom'>): { from: Date; to: Date } {
  const to = new Date();
  if (key === '90d') return { from: new Date(Date.now() - 90 * 86400000), to };
  if (key === '1yr') return { from: new Date(Date.now() - 365 * 86400000), to };
  return { from: ALL_START, to };
}

interface ChartDataPoint {
  timestamp: number;
  cumulativeGallons: number | null;
  lcdRead: number | null;
}

interface ReadTooltipPayload {
  payload: ChartDataPoint;
}

function ReadTooltip(
  { active, payload, lcdUnit }: {
    active?: boolean;
    payload?: ReadTooltipPayload[];
    lcdUnit: string;
  },
) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3 text-sm'>
      <p className='text-muted-foreground mb-1'>{new Date(data.timestamp).toLocaleString()}</p>
      <div className='grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5'>
        <span className='text-muted-foreground'>Lifetime read</span>
        <span className='font-mono text-right' style={{ color: 'hsl(var(--primary))' }}>
          {data.cumulativeGallons != null
            ? `${
              data.cumulativeGallons.toLocaleString(undefined, { maximumFractionDigits: 2 })
            } gal`
            : '—'}
        </span>
        <span className='text-muted-foreground'>LCD read</span>
        <span className='font-mono text-right' style={{ color: 'hsl(var(--chart-3))' }}>
          {data.lcdRead != null
            ? `${data.lcdRead.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${lcdUnit}`
            : '—'}
        </span>
      </div>
    </div>
  );
}

function SummaryBox({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-lg border bg-card p-4'>
      <p className='text-xs font-medium text-muted-foreground'>{label}</p>
      <p className='text-2xl font-bold mt-1 tabular-nums'>{value}</p>
    </div>
  );
}

const CHART_HEIGHT = 350;

export function MeterLifetimeConsumptionChart(
  { sensorId, dateRange: controlledRange }: MeterLifetimeConsumptionChartProps,
) {
  const controlled = controlledRange != null;
  const [preset, setPreset] = useState<PresetKey>('all');
  const [internalRange, setInternalRange] = useState<{ from: Date; to: Date }>(() =>
    presetRange('all')
  );
  const dateRange = controlledRange ?? internalRange;

  const { data, isLoading, error } = useMeterReads(sensorId, dateRange);
  const reads = data?.reads;
  const summary = data?.summary ?? null;
  const lcdUnit = unitLabel(summary?.lcdReadUnit ?? reads?.[reads.length - 1]?.lcdReadUnit);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!reads) return [];
    return reads.map((r) => ({
      timestamp: new Date(r.readingTime).getTime(),
      cumulativeGallons: r.cumulativeGallons,
      lcdRead: r.lcdRead,
    }));
  }, [reads]);

  // Same fractional padding on both axes so a pure unit difference overlaps.
  const cumulativeDomain = useMemo(
    () => paddedDomain(chartData.map((d) => d.cumulativeGallons)),
    [chartData],
  );
  const lcdDomain = useMemo(
    () => paddedDomain(chartData.map((d) => d.lcdRead)),
    [chartData],
  );

  function applyPreset(key: Exclude<PresetKey, 'custom'>) {
    setPreset(key);
    setInternalRange(presetRange(key));
  }

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <CardTitle>Reads</CardTitle>
            <CardDescription>
              Cumulative read and LCD read over time &mdash; useful for spotting LCD rollovers and
              meter events.
            </CardDescription>
          </div>
          {!controlled && (
            <div className='flex items-center gap-2'>
              <div className='flex rounded-md border overflow-hidden'>
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type='button'
                    onClick={() => applyPreset(p.key)}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium border-r last:border-r-0 transition-colors',
                      preset === p.key
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={preset === 'custom' ? 'default' : 'outline'}
                    size='sm'
                    className='gap-2 h-9'
                  >
                    <CalendarIcon className='h-4 w-4' />
                    Custom range
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='end'>
                  <Calendar
                    mode='range'
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range: DateRange | undefined) => {
                      if (range?.from && range?.to) {
                        setPreset('custom');
                        setInternalRange({ from: range.from, to: range.to });
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6'>
          <SummaryBox label='LCD read' value={formatLcdRead(summary?.lcdRead, lcdUnit)} />
          <SummaryBox label='Last 7 days' value={formatGallons(summary?.last7Days)} />
          <SummaryBox label='Last 30 days' value={formatGallons(summary?.last30Days)} />
          <SummaryBox label='Last 90 days' value={formatGallons(summary?.last90Days)} />
        </div>

        {isLoading && (
          <div className='flex items-center justify-center' style={{ height: CHART_HEIGHT }}>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            <span className='ml-2 text-sm text-muted-foreground'>Loading reads...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className='flex items-center justify-center' style={{ height: CHART_HEIGHT }}>
            <p className='text-sm text-destructive'>Failed to load reads</p>
          </div>
        )}

        {!isLoading && !error && chartData.length === 0 && (
          <div className='flex items-center justify-center' style={{ height: CHART_HEIGHT }}>
            <p className='text-sm text-muted-foreground'>No reads available for this range</p>
          </div>
        )}

        {!isLoading && !error && chartData.length > 0 && (
          <>
            <ResponsiveContainer width='100%' height={CHART_HEIGHT}>
              <LineChart data={chartData} margin={{ left: 5, right: 5 }}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis
                  dataKey='timestamp'
                  type='number'
                  scale='time'
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 11 }}
                  minTickGap={40}
                  tickFormatter={(ts) =>
                    new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                />
                <YAxis
                  yAxisId='cumulative'
                  tick={{ fontSize: 11 }}
                  width={80}
                  // Lifetime read is a monotonic odometer (often ~10M+); a
                  // zero-based axis flattens its real movement, so fit to the
                  // data range. Exact data min/max (padded) so it stays in
                  // lockstep with the LCD axis when only the unit differs.
                  domain={cumulativeDomain ?? ['auto', 'auto']}
                  allowDecimals={false}
                  tickFormatter={formatCompact}
                  label={{
                    value: 'lifetime read (gal)',
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      fontSize: 11,
                      fill: 'hsl(var(--muted-foreground))',
                      textAnchor: 'middle',
                    },
                  }}
                />
                <YAxis
                  yAxisId='lcd'
                  orientation='right'
                  tick={{ fontSize: 11 }}
                  width={80}
                  // Same fractional padding as the cumulative axis so a pure
                  // unit difference overlaps exactly; a rollover still diverges.
                  domain={lcdDomain ?? ['auto', 'auto']}
                  allowDecimals={false}
                  tickFormatter={formatCompact}
                  label={{
                    value: `LCD read${lcdUnit ? ` (${lcdUnit})` : ''}`,
                    angle: 90,
                    position: 'insideRight',
                    style: {
                      fontSize: 11,
                      fill: 'hsl(var(--muted-foreground))',
                      textAnchor: 'middle',
                    },
                  }}
                />
                <Tooltip content={<ReadTooltip lcdUnit={lcdUnit} />} />
                <Line
                  yAxisId='cumulative'
                  type='linear'
                  dataKey='cumulativeGallons'
                  stroke='hsl(var(--primary))'
                  strokeWidth={1.5}
                  // Dot at each actual read sample so it's clear where real
                  // values are versus interpolation across gaps.
                  dot={{ r: 3, strokeWidth: 0, fill: 'hsl(var(--primary))' }}
                  connectNulls
                />
                <Line
                  yAxisId='lcd'
                  type='linear'
                  dataKey='lcdRead'
                  stroke='hsl(var(--chart-3))'
                  strokeWidth={1.5}
                  // Dashed so the solid lifetime-read line stays visible
                  // underneath when the two curves coincide (no rollover in
                  // window). They separate visibly when a rollover occurs.
                  strokeDasharray='4 3'
                  dot={{ r: 3, strokeWidth: 0, fill: 'hsl(var(--chart-3))' }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>

            <div className='flex flex-wrap gap-4 mt-4 text-xs'>
              <div className='flex items-center gap-1.5'>
                <div
                  className='w-4 h-0 border-t-2'
                  style={{ borderColor: 'hsl(var(--primary))' }}
                />
                <span className='text-muted-foreground'>Lifetime read (gal)</span>
              </div>
              <div className='flex items-center gap-1.5'>
                <div
                  className='w-4 h-0 border-t-2 border-dashed'
                  style={{ borderColor: 'hsl(var(--chart-3))' }}
                />
                <span className='text-muted-foreground'>
                  LCD read{lcdUnit ? ` (${lcdUnit})` : ''}
                </span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
